// apps/client/src/world/digging.ts (M2-T09) — the 'digging' layer of the OutdoorScene: crack decals on your personal dig spots
// (PersonalYields.digSpots). Other residents' spots never reach this client, so they never show.
import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import type { DigSpotView, Vec2 } from '@whiskerton/shared';
import { toyMaterial } from '../characters/doll';
import { patchObject } from '../engine/shader/worldPatch';
import type { Game } from '../game/game';

export interface DigLayer { readonly group: THREE.Group; set(spots: readonly DigSpotView[]): void; hide(id: string, hidden: boolean): void; ids(): string[] }

let crackGeo: THREE.BufferGeometry | null = null;
/** A small star of dark grooves with two pebbles: reads as "dig here" against grass and sand. */
function crackGeometry(): THREE.BufferGeometry {
  if (crackGeo) return crackGeo;
  const parts: THREE.BufferGeometry[] = [];
  for (const [len, rot] of [[0.55, 0.2], [0.42, 1.3], [0.36, 2.4], [0.3, -0.9]] as const) {
    const b = new THREE.BoxGeometry(len, 0.02, 0.06);
    b.translate(len / 2 - 0.04, 0, 0);
    b.rotateY(rot);
    parts.push(b);
  }
  for (const [x, z] of [[0.18, 0.16], [-0.2, -0.12]] as const) parts.push(new THREE.SphereGeometry(0.045, 8, 6).translate(x, 0.02, z));
  crackGeo = mergeGeometries(parts.map((p) => p.toNonIndexed()))!;
  return crackGeo;
}

export function createDigLayer(group: THREE.Group, heightAt: (p: Vec2) => number): DigLayer {
  const mat = toyMaterial('#6B4F3A');
  const byId = new Map<string, THREE.Mesh>();
  return {
    group,
    set(spots) {
      for (const m of byId.values()) group.remove(m);
      byId.clear();
      for (const s of spots) {
        const m = new THREE.Mesh(crackGeometry(), mat);
        m.name = `dig:${s.id}`;
        const p = { x: s.x + 0.5, y: s.y + 0.5 };
        m.position.set(p.x, heightAt(p) + 0.01, p.y);
        m.rotation.y = ((s.x * 73 + s.y * 37) % 360) * (Math.PI / 180);   // stable variety per tile
        m.receiveShadow = true;
        patchObject(m);
        group.add(m);
        byId.set(s.id, m);
      }
    },
    hide(id, hidden) { const m = byId.get(id); if (m) m.visible = !hidden; },
    ids: () => [...byId.keys()],
  };
}

const layers = new WeakMap<THREE.Group, DigLayer>();
export function digLayerOf(game: Game): DigLayer | null {
  const out = game.scenes.out;
  if (!out) return null;
  const group = out.layer('digging');
  let l = layers.get(group);
  if (!l) { l = createDigLayer(group, (p) => out.heightAt(p)); layers.set(group, l); }
  return l;
}
